import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import i18n, {
  urlLocaleToI18n,
  i18nLocaleToUrl,
  getLocaleFromPath,
  isValidUrlLocale,
  setLocale,
  getCurrentLocale
} from '@/plugins/i18n'

// Layout components
import AuthLayout from '@/layouts/AuthLayout.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import GuestLayout from '@/layouts/GuestLayout.vue'

// Auth pages (keep these eager loaded as they're entry points)
import Login from '@/pages/auth/Login.vue'
import Register from '@/pages/auth/Register.vue'
import ForgotPassword from '@/pages/auth/ForgotPassword.vue'
import VerifyEmail from '@/pages/auth/VerifyEmail.vue'
import ResetPassword from '@/pages/auth/ResetPassword.vue'

// Guest pages (keep welcome eager)
import Welcome from '@/pages/Welcome.vue'

// Lazy-loaded app pages for better performance
const Dashboard = () => import('@/pages/Dashboard.vue')
const Campaigns = () => import('@/pages/campaigns/Index.vue')
const CampaignCreate = () => import('@/pages/campaigns/Create.vue')
const CampaignShow = () => import('@/pages/campaigns/Show.vue')
const CampaignEdit = () => import('@/pages/campaigns/Edit.vue')
const Analytics = () => import('@/pages/analytics/Index.vue')
const Contacts = () => import('@/pages/contacts/Index.vue')
const ContactCreate = () => import('@/pages/contacts/Create.vue')
const ContactEdit = () => import('@/pages/contacts/Edit.vue')
const Templates = () => import('@/pages/templates/Index.vue')
const TemplateCreate = () => import('@/pages/templates/Create.vue')
const TemplateEdit = () => import('@/pages/templates/Edit.vue')
const TemplateBuilder = () => import('@/pages/templates/Builder.vue')
const Automations = () => import('@/pages/automations/Index.vue')
const AutomationCreate = () => import('@/pages/automations/Create.vue')
const Settings = () => import('@/pages/Settings.vue')
const Profile = () => import('@/pages/Profile.vue')
const ApiDocs = () => import('@/pages/ApiDocs.vue')

// Admin pages
const AdminDashboard = () => import('@/pages/admin/Dashboard.vue')
const AdminUsers = () => import('@/pages/admin/Users.vue')
const AdminPlans = () => import('@/pages/admin/Plans.vue')
const AdminAnalytics = () => import('@/pages/admin/Analytics.vue')
const AdminSettings = () => import('@/pages/admin/Settings.vue')
const AdminLogs = () => import('@/pages/admin/Logs.vue')
const AdminMonitoring = () => import('@/pages/admin/Monitoring.vue')

// Locale pattern for route matching
const LOCALE_PATTERN = 'en|pt-br|es'

// Simple passthrough component for locale wrapper
import { h, resolveComponent } from 'vue'
const LocaleWrapper = { render: () => h(resolveComponent('router-view')) }

const routes = [
  // Root redirect to locale-prefixed URL
  {
    path: '/',
    redirect: () => {
      const currentLocale = getCurrentLocale()
      const urlLocale = i18nLocaleToUrl(currentLocale)
      return `/${urlLocale}`
    }
  },

  // Locale-prefixed routes
  {
    path: `/:locale(${LOCALE_PATTERN})`,
    component: LocaleWrapper,
    children: [
      // Guest routes (Welcome page)
      {
        path: '',
        component: GuestLayout,
        children: [
          {
            path: '',
            name: 'welcome',
            component: Welcome,
            meta: { guest: true }
          }
        ]
      },

      // Auth routes
      {
        path: 'auth',
        component: AuthLayout,
        children: [
          {
            path: 'login',
            name: 'login',
            component: Login,
            meta: { guest: true }
          },
          {
            path: 'register',
            name: 'register',
            component: Register,
            meta: { guest: true }
          },
          {
            path: 'forgot-password',
            name: 'forgot-password',
            component: ForgotPassword,
            meta: { guest: true }
          },
          {
            path: 'reset-password',
            name: 'password.reset',
            component: ResetPassword,
            meta: { guest: true }
          },
          {
            path: 'verify-email',
            name: 'verify-email',
            component: VerifyEmail,
            meta: { requiresAuth: true }
          }
        ]
      },

      // Public Documentation
      {
        path: 'docs',
        name: 'api-docs',
        component: ApiDocs,
        meta: { guest: true }
      },

      // App routes (authenticated)
      {
        path: 'app',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
          {
            path: 'dashboard',
            name: 'dashboard',
            component: Dashboard,
            meta: {
              breadcrumbs: [
                { label: 'Dashboard' }
              ]
            }
          },
          {
            path: 'campaigns',
            name: 'campaigns',
            component: Campaigns,
            meta: {
              breadcrumbs: [
                { label: 'Dashboard', to: { name: 'dashboard' } },
                { label: 'Campaigns' }
              ]
            }
          },
          {
            path: 'campaigns/create',
            name: 'campaigns.create',
            component: CampaignCreate
          },
          {
            path: 'campaigns/:id',
            name: 'campaigns.show',
            component: CampaignShow,
            props: true
          },
          {
            path: 'campaigns/:id/edit',
            name: 'campaigns.edit',
            component: CampaignEdit,
            props: true
          },
          {
            path: 'analytics',
            name: 'analytics',
            component: Analytics,
            meta: {
              breadcrumbs: [
                { label: 'Dashboard', to: { name: 'dashboard' } },
                { label: 'Analytics' }
              ]
            }
          },
          {
            path: 'contacts',
            name: 'contacts',
            component: Contacts
          },
          {
            path: 'contacts/create',
            name: 'contacts.create',
            component: ContactCreate
          },
          {
            path: 'contacts/:id/edit',
            name: 'contacts.edit',
            component: ContactEdit,
            props: true
          },
          {
            path: 'templates',
            name: 'templates',
            component: Templates
          },
          {
            path: 'templates/create',
            name: 'templates.create',
            component: TemplateCreate
          },
          {
            path: 'templates/builder',
            name: 'templates.builder',
            component: TemplateBuilder
          },
          {
            path: 'templates/:id/edit',
            name: 'templates.edit',
            component: TemplateEdit,
            props: true
          },
          {
            path: 'templates/:id/builder',
            name: 'templates.edit-builder',
            component: TemplateBuilder,
            props: true
          },
          {
            path: 'automations',
            name: 'automations',
            component: Automations
          },
          {
            path: 'automations/create',
            name: 'automations.create',
            component: AutomationCreate
          },
          {
            path: 'settings',
            name: 'settings',
            component: Settings
          },
          {
            path: 'profile',
            name: 'profile',
            component: Profile
          }
        ]
      },

      // Admin routes (role-based access) - using unified MainLayout
      {
        path: 'admin',
        component: MainLayout,
        meta: { requiresAuth: true, requiresAdmin: true },
        children: [
          {
            path: 'dashboard',
            name: 'admin.dashboard',
            component: AdminDashboard,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard' }
              ]
            }
          },
          {
            path: 'users',
            name: 'admin.users',
            component: AdminUsers,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'User Management' }
              ]
            }
          },
          {
            path: 'plans',
            name: 'admin.plans',
            component: AdminPlans,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'Subscription Plans' }
              ]
            }
          },
          {
            path: 'analytics',
            name: 'admin.analytics',
            component: AdminAnalytics,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'System Analytics' }
              ]
            }
          },
          {
            path: 'settings',
            name: 'admin.settings',
            component: AdminSettings,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'System Settings' }
              ]
            }
          },
          {
            path: 'logs',
            name: 'admin.logs',
            component: AdminLogs,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'System Logs' }
              ]
            }
          },
          {
            path: 'monitoring',
            name: 'admin.monitoring',
            component: AdminMonitoring,
            meta: {
              breadcrumbs: [
                { label: 'Admin Dashboard', to: { name: 'admin.dashboard' } },
                { label: 'Monitoring' }
              ]
            }
          }
        ]
      }
    ]
  },

  // Legacy redirects for backward compatibility (without locale)
  {
    path: '/dashboard',
    redirect: () => {
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      return `/${urlLocale}/app/dashboard`
    }
  },
  {
    path: '/app/:pathMatch(.*)*',
    redirect: (to) => {
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      return `/${urlLocale}${to.fullPath}`
    }
  },
  {
    path: '/auth/:pathMatch(.*)*',
    redirect: (to) => {
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      return `/${urlLocale}${to.fullPath}`
    }
  },
  {
    path: '/admin/:pathMatch(.*)*',
    redirect: (to) => {
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      return `/${urlLocale}${to.fullPath}`
    }
  },
  {
    path: '/docs',
    redirect: () => {
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      return `/${urlLocale}/docs`
    }
  },

  // 404 catch all - redirect based on auth status
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore()
      const isAuthenticated = authStore.checkAuthentication()
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())

      console.log('[Router] 404 catch-all triggered:', {
        path: to.path,
        fromName: from.name,
        isAuthenticated
      })

      // Only redirect if coming from a real route attempt (not initial load)
      if (from.name !== null) {
        if (isAuthenticated) {
          const redirectPath = authStore.isAdmin ? `/${urlLocale}/admin/dashboard` : `/${urlLocale}/app/dashboard`
          console.log('[Router] Redirecting authenticated user from 404 to:', redirectPath)
          next(redirectPath)
        } else {
          console.log('[Router] Redirecting unauthenticated user from 404 to login')
          next({ name: 'login', params: { locale: urlLocale } })
        }
      } else {
        // Initial load of invalid URL - redirect to home page
        console.log('[Router] Initial load of invalid URL, redirecting to home')
        next(`/${urlLocale}`)
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Sync locale from URL parameter
  const urlLocale = to.params.locale
  if (urlLocale && isValidUrlLocale(urlLocale)) {
    const i18nLocale = urlLocaleToI18n(urlLocale)
    if (i18n.global.locale.value !== i18nLocale) {
      setLocale(i18nLocale)
    }
  }

  console.log('[Router] Navigation:', {
    from: from.path,
    to: to.path,
    toName: to.name,
    fromName: from.name,
    locale: urlLocale,
    requiresAuth: to.meta.requiresAuth,
    guest: to.meta.guest,
    isAuthenticated: authStore.checkAuthentication(),
    hasToken: !!authStore.token,
    hasUser: !!authStore.user,
    userRole: authStore.user?.role
  })

  // Track redirect history to prevent infinite loops
  const MAX_REDIRECTS = 3
  if (!router.redirectHistory) {
    router.redirectHistory = []
  }

  // Add current navigation to history
  router.redirectHistory.push({
    to: to.path,
    from: from.path,
    time: Date.now()
  })

  // Keep only recent history (last 5 seconds)
  const fiveSecondsAgo = Date.now() - 5000
  router.redirectHistory = router.redirectHistory.filter(h => h.time > fiveSecondsAgo)

  // Check if we're in a redirect loop (same pattern repeating)
  const recentRedirects = router.redirectHistory.slice(-MAX_REDIRECTS)
  if (recentRedirects.length >= MAX_REDIRECTS) {
    const allSame = recentRedirects.every(r => r.to === to.path && r.from === from.path)
    if (allSame) {
      console.error('[Router] Redirect loop detected:', to.path, 'from:', from.path)
      // Force redirect to safe page
      const safeLocale = i18nLocaleToUrl(getCurrentLocale())
      if (!to.path.includes('/auth/login')) {
        console.log('[Router] Breaking loop by redirecting to login')
        return next(`/${safeLocale}/auth/login`)
      }
      return next(false)
    }
  }

  // Get current URL locale for redirects
  const currentUrlLocale = urlLocale || i18nLocaleToUrl(getCurrentLocale())

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    const isAuthenticated = authStore.checkAuthentication()

    if (!isAuthenticated) {
      console.log('[Router] Not authenticated, redirecting to login')
      return next({ name: 'login', params: { locale: currentUrlLocale }, query: { redirect: to.fullPath } })
    }

    // Check if route requires admin access
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      console.log('[Router] User is not admin, redirecting to user dashboard')
      return next({ name: 'dashboard', params: { locale: currentUrlLocale } })
    }
  }

  // Check if route is for guests only
  if (to.meta.guest) {
    const isAuthenticated = authStore.checkAuthentication()
    if (isAuthenticated) {
      // Prevent redirect if already going to a dashboard
      if (to.path.includes('/app/dashboard') || to.path.includes('/admin/dashboard')) {
        console.log('[Router] Already navigating to dashboard, allowing')
        return next()
      }

      // Redirect authenticated users to appropriate dashboard
      const dashboardName = authStore.isAdmin ? 'admin.dashboard' : 'dashboard'
      console.log('[Router] Guest route accessed by authenticated user, redirecting to:', dashboardName)
      return next({ name: dashboardName, params: { locale: currentUrlLocale } })
    }
  }

  console.log('[Router] Navigation allowed to:', to.path)
  next()
})

export default router